<template lang="pug">

  v-form(ref="form" v-model="valid" lazy-validation)
    v-card
      v-card-title
        span.headline {{ $t('task.head' )}}
        v-spacer
        v-btn(icon @click="close"): v-icon close
      v-card-text
        v-container(grid-list-md)
          v-layout(wrap)
            v-flex(xs8)
              v-autocomplete(
                v-model="client_id"
                :items="clients"
                :label="$t('task.client')"
                item-text="name"
                item-value="id"
                :rules="rules.client_id"
                :error-messages="errors.client_id"
              )
            v-flex(xs4)
              v-select(
                v-model="category_id"
                :items="categories"
                :label="$t('task.category')"
                item-text="name"
                item-value="id"
                :rules="rules.category_id"
                :error-messages="errors.category_id"
              )
            v-flex(xs12)
              v-text-field(
                v-model="title"
                :label="$t('task.title')"
                :rules="rules.title"
                :error-messages="errors.title"
              )
            v-flex(xs12)
              v-textarea(
                v-model="description"
                :label="$t('task.description')"
                :rules="rules.description"
                :error-messages="errors.description"
                no-resize
                counter="500"
              )
            v-flex(xs12 sm5)
              v-menu(
                :close-on-content-click="false"
                v-model="datePicker"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              )
                template(v-slot:activator="{ on }")
                  v-text-field(
                    v-model="date"
                    :label="$t('task.date')"
                    persistent-hint
                    prepend-icon="event"
                    :rules="rules.date"
                    :error-messages="errors.starts_at"
                    v-on="on"
                  )
                v-date-picker(
                  v-model="date"
                  first-day-of-week="1"
                  no-title
                  @input="datePicker=false"
                  :min="$moment().format('YYYY-MM-DD')"
                  :allowed-dates="allowedDates"
                  :locale="$i18n.locale"
                )
            v-flex(xs11 sm3)
              v-select(
                v-model="assigned_to_id"
                :items="users"
                :label="$t('task.assigned_to')"
                item-text="name"
                item-value="id"
                :error-messages="errors.assigned_to_id"
              )
            v-flex(xs11 sm3)
              v-select(
                v-model="owner_id"
                :items="all_users"
                :label="$t('task.owner')"
                item-text="name"
                item-value="id"
                :error-messages="errors.owner_id"
              )
            v-flex(xs1 text-right)
              v-tooltip(top)
                template(v-slot:activator="{ on }")
                  v-checkbox(v-model="done" v-on="on")
                span {{ $t('task.done') }}
            v-flex(xs12)
              v-range-slider(
                :rules="rules.time"
                :tick-labels="hours.map((item, i) => ''+i)"
                v-model="time"
                always-dirty
                min="0"
                :max="hours.length-1"
                ticks="always"
                :error-messages="errors.duration"
              )

      v-card-actions
        v-menu(v-if="data.id" auto)
          template(v-slot:activator="{ on }")
            v-btn(color="red" text v-on="on") {{ $t('ui.delete') }}
          v-list.error
            v-list-item(@click="remove"): v-list-item-title {{ $t('ui.are_you_sure') }}
        v-btn(text @click="close") {{ $t('ui.cancel') }}
        v-spacer
        v-btn(color="blue" text :disabled="!valid" @click="submit") {{ $t('ui.save') }}

</template>

<script>
import { zeroFill } from 'w3box-utils'
import { mapGetters, mapActions } from 'vuex'

export default{

  props: {
    data: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      valid: true,
      datePicker: false,
      category_id: undefined,
      client_id: undefined,
      assigned_to_id: undefined,
      owner_id: undefined,
      title: '',
      description: '',
      done: false,
      date: '',
      time: '',
      rules: {
        category_id: [
          v => true,
        ],
        client_id: [
          v => !!v || this.$t('validation.required', { field: this.$t('task.client') }),
        ],
        title: [
          v => !!v || this.$t('validation.required', { field: this.$t('task.title') }),
        ],
        description: [
          // v => !!v || this.$t('validation.required', { field: this.$t('task.description') }),
        ],
        date: [
          v => !!v || this.$t('validation.required', { field: this.$t('task.date') }),
          v => v && !!v.match(/^\d{4}-\d{2}-\d{2}$/) || this.$t('validation.date_pattern'),
          v => v && !this.getHoliday(this.$moment(v)) || this.$t('validation.holiday'),
          v => v && this.$moment(v).weekday() < 5 || this.$t('validation.weekend'),
        ],
        time: [
          v => v[0] != v[1] || this.$t('validation.zero_time'),
        ],
      },
      errors: {}
    }
  },

  computed: {
    ...mapGetters('auth', ['user']),
    ...mapGetters('traffic', [
      'categories',
      'clients',
      'getHoliday',
      'users',
    ]),
    all_users() {
      return this.$store.getters['entities/users/query']().orderBy('name').get()
    },
    hours() {
      return this.$store.getters['traffic/hours'](null, true).map(hour => this.$moment(hour).format('H:mm'))
    },
  },

  watch: {
    category_id() {
      this.errors.category_id = undefined
    },
    client_id() {
      this.errors.client_id = undefined
    },
    title() {
      this.errors.title = undefined
    },
    description() {
      this.errors.description = undefined
    },
    assigned_to_id() {
      this.errors.assigned_to_id = undefined
    },
    owner_id() {
      this.errors.owner_id = undefined
    },
    date() {
      this.errors.starts_at = undefined
    },
    time() {
      this.errors.duration = undefined
    },
  },

  beforeMount() {
    this.category_id = this.data.category_id
    this.client_id = this.data.client_id
    this.assigned_to_id = this.data.assigned_to_id
    this.owner_id = this.data.id ? this.data.owner_id : this.user.id
    this.title = this.data.title
    this.description = this.data.description
    this.date = this.data.starts_at.substr(0, 10)
    this.done = this.data.done
    this.time = [
      this.hours.indexOf(this.$moment(this.data.starts_at).format('H:mm')),
      this.hours.indexOf(this.$moment(this.data.ends_at).format('H:mm'))
    ]
  },

  methods: {
    ...mapActions('entities/tasks', [
      'createItem',
      'updateItem',
      'deleteItem',
    ]),
    allowedDates(val) {
      const day = this.$moment(val)
      return !(day.weekday() > 4 || this.getHoliday(day.valueOf()))
    },
    close() {
      this.$emit('close', !!this.data.id)
    },
    submit() {
      if(this.$refs.form.validate()) {
        const data = {
          ...this.data,
          category_id: this.category_id,
          client_id: this.client_id,
          assigned_to_id: this.assigned_to_id,
          owner_id: this.owner_id,
          title: this.title,
          description: this.description,
          starts_at: this.date + ' ' + zeroFill(this.hours[this.time[0]], 5) + ':00',
          ends_at: this.date + ' ' + zeroFill(this.hours[this.time[1]], 5) + ':00',
          done: this.done,
        }
        if(!data.description) delete data.description
        let promise
        if(this.data.id) promise = this.updateItem(data)
        else             promise = this.createItem(data)
        promise
          .then(response => this.$emit('close', true))
          .catch(failed => this.errors = failed.errors || {})
      }
    },
    remove() {
      this.deleteItem(this.data).then(response => this.$emit('close', false))
    },
  },
}
</script>